import { useState } from "react";
import { toast } from "sonner";
import { WhatsappLogo, EnvelopeSimple, Globe, Phone, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent! We'll respond within 24 hours.");
            setSubmitted(true);
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white" data-testid="contact-page">
            <section className="bg-[#0A192F] pt-32 md:pt-40 pb-16 relative overflow-hidden bg-grain">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Get In Touch</div>
                    <h1 className="font-display text-4xl md:text-6xl font-bold text-white max-w-3xl leading-tight">
                        Let's talk <span className="text-gold-gradient">strategy.</span>
                    </h1>
                    <p className="text-white/70 mt-4 max-w-2xl">
                        WhatsApp us for the fastest response, or send a message via email or the form below.
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="max-w-6xl mx-auto px-6 md:px-8 grid lg:grid-cols-5 gap-10">
                    {/* Contact info */}
                    <div className="lg:col-span-2 space-y-5">
                        <a href="https://wa.me/18457295009?text=Hi%20CashFlow%20Ads" target="_blank" rel="noopener noreferrer" className="block bg-[#25D366] hover:bg-[#1ebe5b] rounded-2xl p-6 text-white transition-all card-lift" data-testid="contact-whatsapp-card">
                            <WhatsappLogo size={32} weight="fill" />
                            <div className="text-xs uppercase tracking-[0.2em] opacity-80 mt-3">WhatsApp · Fastest</div>
                            <div className="font-display text-2xl font-bold mt-1">845-729-5009</div>
                            <div className="text-sm opacity-90 mt-2 inline-flex items-center gap-1">Chat now <ArrowRight size={14} /></div>
                        </a>
                        <a href="mailto:cashflowads613@gmail.com" className="block bg-[#0A192F] hover:bg-[#112240] rounded-2xl p-6 text-white transition-all card-lift" data-testid="contact-email-card">
                            <EnvelopeSimple size={32} weight="duotone" color="#D4AF37" />
                            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mt-3">Email</div>
                            <div className="font-display text-lg md:text-xl font-bold mt-1 break-all">cashflowads613@gmail.com</div>
                        </a>
                        <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100">
                            <Globe size={28} weight="duotone" color="#0A192F" />
                            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-3">Website</div>
                            <div className="font-display text-lg font-bold mt-1 text-[#0A192F]">cashflow-ads.com</div>
                        </div>
                        <a href="https://wa.me/18457295009?text=Hi%2C%20I%27d%20like%20to%20schedule%20a%20call." target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-br from-[#D4AF37] to-[#b8951e] rounded-2xl p-6 text-[#0A192F] card-lift" data-testid="contact-schedule-call">
                            <Phone size={28} weight="duotone" />
                            <div className="font-display text-lg font-bold mt-3">Schedule a call</div>
                            <div className="text-sm mt-1 opacity-80">Book a 15-min strategy session.</div>
                        </a>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-3">
                        {submitted ? (
                            <div className="bg-[#0A192F] text-white rounded-3xl p-12 text-center" data-testid="contact-success">
                                <CheckCircle size={56} weight="fill" color="#D4AF37" className="mx-auto" />
                                <h3 className="font-display text-2xl font-bold mt-4">Message sent!</h3>
                                <p className="text-white/70 mt-2">We'll get back to you within 24 hours.</p>
                                <button onClick={() => setSubmitted(false)} className="mt-6 px-5 py-2.5 rounded-full bg-[#D4AF37] text-[#0A192F] font-semibold text-sm" data-testid="contact-submit-another">
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-xl space-y-5" data-testid="contact-form">
                                <h3 className="font-display text-2xl font-bold text-[#0A192F]">Send us a message</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="c-name" className="text-[#0A192F] font-semibold">Name</Label>
                                        <Input id="c-name" required value={form.name} onChange={update("name")} className="mt-1.5" data-testid="contact-input-name" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-email" className="text-[#0A192F] font-semibold">Email</Label>
                                        <Input id="c-email" type="email" required value={form.email} onChange={update("email")} className="mt-1.5" data-testid="contact-input-email" />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="c-phone" className="text-[#0A192F] font-semibold">Phone</Label>
                                        <Input id="c-phone" value={form.phone} onChange={update("phone")} className="mt-1.5" data-testid="contact-input-phone" />
                                    </div>
                                    <div>
                                        <Label htmlFor="c-subj" className="text-[#0A192F] font-semibold">Subject</Label>
                                        <Input id="c-subj" value={form.subject} onChange={update("subject")} className="mt-1.5" data-testid="contact-input-subject" />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="c-msg" className="text-[#0A192F] font-semibold">Message</Label>
                                    <Textarea id="c-msg" required rows={5} value={form.message} onChange={update("message")} className="mt-1.5" data-testid="contact-input-message" />
                                </div>
                                <Button type="submit" disabled={submitting} className="w-full bg-[#0A192F] hover:bg-[#112240] text-white font-semibold py-6 rounded-full text-base" data-testid="contact-submit-button">
                                    {submitting ? "Sending..." : "Send Message"}
                                </Button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
